<template>
  <div class="task-due-notifications-container" data-testid="task-due-notifications">
    <div v-if="loading" class="notification-loading" data-testid="task-due-notifications__loading-spinner">
      <v-skeleton-loader
        data-testid="task-due-notifications__loading-skeleton"
        type="table-heading, list-item-three-line, article"
        :loading="loading"
        width="100%"
      />
    </div>
    <div v-else-if="error" class="notification-error" data-testid="task-due-notifications__error-alert">
      {{ error }}
    </div>
    <div v-else data-testid="task-due-notifications__main-panel">
      <v-card class="shadow-off" :loading="loading" :disabled="loading" data-testid="task-due-notifications__card">
        <v-card-text v-if="notificationEnabled" data-testid="task-due-notifications__card-text">
          <div class="d-flex mb-2" data-testid="task-due-notifications__toolbar">
            <div
              class="d-flex align-center"
              v-if="allCreatedNotifications.length > 0"
              data-testid="task-due-notifications__notification-count-container"
            >
              <h2 class="font-weight-medium" data-testid="task-due-notifications__notification-count">
                {{ allCreatedNotifications.length }}
              </h2>
              <span class="ml-2 line-1" data-testid="task-due-notifications__notification-count-label">
                Notification<br />count
              </span>
            </div>
            <v-spacer data-testid="task-due-notifications__toolbar-spacer"></v-spacer>
            <v-btn
              color="primary"
             
              class="mb-2"
              @click="openDialog()"
              data-testid="task-due-notifications__create-btn"
            >
              <span>Create notification</span>
            </v-btn>
            <v-btn
              class="shadow-off"
              icon
              @click="refreshNotifications()"
              data-testid="task-due-notifications__refresh-btn"
            >
              <v-icon data-testid="task-due-notifications__refresh-icon">mdi-refresh</v-icon>
            </v-btn>
          </div>
          <TaskDueNotificationTable
            data-testid="task-due-notifications__table"
            :notifications="allCreatedNotifications"
            :headers="headers"
            :loading="loading"
            @edit="editNotification"
            @toggle="toggleNotification"
          />
        </v-card-text>
        <NotificationNotEnabledMessage
          v-else
          :message="notificationDisabledMessage"
          data-testid="task-due-notifications__not-enabled-message"
        />
      </v-card>

      <!-- Task Notification All Dialog Component -->
      <EmailNotificationDialog
        data-testid="task-due-notifications__notification-all-dialog"
        :showTypeDialog="showNotificationDialog"
        :locations="locations"
        :categories="categories"
        :roles="roles"
        :timeList="timeList"
        :editData="editNotificationData"
        @close="handleDialogClose"
        @submit-success="handleSubmitSuccess"
      />
    </div>
  </div>
</template>

<script>
import TaskDueNotificationTable from "./TaskDueNotificationTable.vue";
import NotificationNotEnabledMessage from "../../components/NotificationNotEnabledMessage/NotificationNotEnabledMessage.vue";
import EmailNotificationDialog from "./EmailNotificationDialog.vue";
import {
  CRON,
  dueNotificationHeaders,
  EMAIL_NOTIFICATION,
} from "../../utils/constants";

export default {
  name: "TaskDueNotifications",
  components: {
    TaskDueNotificationTable,
    NotificationNotEnabledMessage,
    EmailNotificationDialog,
  },
  data() {
    return {
      loading: false,
      error: null,
      showNotificationDialog: false,
      editNotificationData: null,
      notificationDisabledMessage:
        EMAIL_NOTIFICATION.MESSAGES.NOTIFICATION_NOT_ENABLED,
      dialogLoading: false,
      headers: dueNotificationHeaders(),
      roles: EMAIL_NOTIFICATION.roles ?? [],
      timeList: EMAIL_NOTIFICATION.timeList ?? [],
      errorMessages: {
        feature: "",
        schedulerTiming: "",
        roles: "",
        locations: "",
      },
    };
  },
  computed: {
    notificationEnabled() {
      return this.$store.getters[
        "taskDueNotifications/fetchNotificationEnabled"
      ];
    },
    categories() {
      return this.$store.getters["taskDueNotifications/fetchCategories"];
    },
    locations() {
      const allLocations =
        this.$store.getters["taskDueNotifications/fetchLocations"];
      return allLocations.map((location) => ({
        value: location._id,
        name: location.name,
      }));
    },
    availableNotifications() {
      const notifications =
        this.$store.getters["taskDueNotifications/fetchAvailableNotifications"];
      return notifications.map((notification) => ({
        ...notification,
        feature: notification.name,
      }));
    },
    allCreatedNotifications() {
      let allNotifications =
        this.$store.getters[
          "taskDueNotifications/fetchAllCreatedNotifications"
        ];
      let notifications = allNotifications.map((notification) => {
        return {
          id: notification._id,
          type: notification.type,
          status: notification.status,
          feature: notification.feature,
          updatedAt: notification.updatedAt,
          labelName: notification.labelName,
          schedulerTiming: CRON.cronFormatToHour(notification.schedulerTiming),
          locations: this.mapLocation(notification.meta?.locations ?? notification.meta?.location_key ?? []),
          roles: this.mapRoles(notification.meta?.roles ?? []),
          // Additional fields for Task List Escalation and Dashboard
          emails: notification.emails || notification.meta?.toUsers || null,
          subject: notification.subject || null,
          body: notification.mailBody || notification.body || "",
          start: notification.meta?.start || "",
          end: notification.meta?.end || "",
          startDate: notification.meta?.start || "",
          endDate: notification.meta?.end || "",
          category: notification.meta?.category || [],
          sla: notification.sla || null,
          priority: notification.priority || null,
          // Store full meta for reference
          meta: notification.meta || {},
        };
      });
      return notifications;
    },
  },
  async created() {
    const actions = [
      "taskDueNotifications/fetchNotificationEnabled",
      "taskDueNotifications/fetchLocations",
      "taskDueNotifications/fetchAllCreatedNotifications",
    ];
    this.loading = true;
    
    // Fetch categories via API call
    await this.fetchCategories();
    
    const results = await Promise.allSettled(
      actions.map((action) => this.$store.dispatch(action))
    );
    const failed = results.filter((result) => result.status === EMAIL_NOTIFICATION.REJECTED);
    if (failed.length > 0) {
      this.error = EMAIL_NOTIFICATION.MESSAGES.FAILED_GET;
      this.$store.commit(
        "sbError",
        failed.map((f) => f.reason).join(", ") || this.error
      );
    }
    this.loading = false;
  },
  beforeDestroy() {
    // Reset component-specific data to default values
    this.resetComponentData();
  },
  methods: {
    /**
     * Fetches categories from chart of accounts API
     */
    async fetchCategories() {
      try {
        await this.$store.dispatch("taskDueNotifications/fetchCategories");
      } catch (error) {
        console.error(
          "[TaskDueNotifications]: Failed to fetch categories",
          error
        );
      }
    },

    /**
     * Opens the notification dialog for creating new notification
     */
    openDialog() {
      this.editNotificationData = null;
      this.showNotificationDialog = true;
    },

    /**
     * Handles dialog close event from child component
     */
    handleDialogClose() {
      this.showNotificationDialog = false;
      this.editNotificationData = null;
    },

    /**
     * Handles successful submission from child component
     */
    async handleSubmitSuccess() {
      this.showNotificationDialog = false;
      this.editNotificationData = null;
    },

    /**
     * Refreshes the notifications list from the store
     */
    async refreshNotifications() {
      this.loading = true;
      this.error = null;
      try {
        await this.$store.dispatch(
          "taskDueNotifications/fetchAllCreatedNotifications"
        );
      } catch (err) {
        this.$store.commit("sbError", err?.message || "Unknown error!");
      } finally {
        this.loading = false;
      }
    },

    /**
     * Opens the notification dialog in edit mode for existing notification
     * @param {Object} item - Notification item to edit
     */
    editNotification(item) {
      try {
        this.showNotificationDialog = true;
        // Extract role values from the mapped roles array
        const roleValues = item?.roles?.map((role) => role?.value) || [];
        // Extract location values from the mapped locations array
        const locationValues = item?.locations?.map((location) => location?.value) ?? [];
        
        // Check if original roles was a string to handle single role case
        const originalRoles = item?.meta?.roles;
        const rolePayload = (typeof originalRoles === 'string' && roleValues.length > 0) 
          ? roleValues[0] 
          : roleValues;

        // Pass complete notification data for editing
        this.editNotificationData = {
          _id: item?.id,
          type: item?.feature,
          feature: item?.feature,
          scheduler: item?.schedulerTiming,
          schedulerTiming: item?.schedulerTiming,
          labelName: item?.labelName,
          status: item?.status,
          flag: item?.status === "enabled" || item?.status === true,
          location: locationValues,
          locations: locationValues,
          role: rolePayload,
          roles: roleValues,
          // Additional fields for Task List Escalation and Dashboard
          emails: item?.emails || null,
          subject: item?.subject || null,
          body: item?.body || "",
          start: item?.start || item?.meta?.start || "",
          end: item?.end || item?.meta?.end || "",
          startDate: item?.startDate || item?.start || item?.meta?.start || "",
          endDate: item?.endDate || item?.end || item?.meta?.end || "",
          category: item?.category || item?.meta?.category || [],
          sla: item?.sla ||item?.meta?.sla || [] || null,
          priority: item?.priority || item?.meta?.priority || 'High',
          hour: item?.schedulerTiming || "",
          // Pass meta for fallback in child component
          meta: item?.meta || {},
        };
      } catch (error) {
        this.$store.commit("sbError", error?.message || "Unknown error!");
      }
    },
    async toggleNotification(item, enable) {
      this.loading = true;
      try {
        await this.$store.dispatch(
          "taskDueNotifications/changeStatusForNotification",
          {
            id: item?.id.toString(),
            status: enable
              ? EMAIL_NOTIFICATION.STATUS.ENABLED
              : EMAIL_NOTIFICATION.STATUS.DISABLED,
          }
        );
        this.$store.commit(
          "sbSuccess",
          EMAIL_NOTIFICATION.MESSAGES.SUCCESS_STATUS_CHANGE
        );
      } catch (err) {
        this.$store.commit("sbError", err?.message || "Unknown error!");
      } finally {
        this.loading = false;
      }
    },
    mapLocation(locations) {
      const allLocations =
        this.$store.getters["taskDueNotifications/fetchLocations"] ?? [];
      const mappedLocations = locations?.map((location) => {
        return {
          value: location,
          name:
            allLocations?.find((loc) => loc?._id === location)?.name ||
            location,
        };
      });
      return mappedLocations;
    },
    mapRoles(roles) {
      const allRoles = this.roles ?? [];
      const rolesList = typeof roles === 'string' ? [roles] : (roles ?? []);
      return rolesList.map((role) => {
        return {
          value: role,
          name: allRoles?.find((r) => r.value === role)?.name || role,
        };
      });
    },
    /**
     * Closes popup and clears all form fields
     */
    closePopupAndClearFields() {
      this.showNotificationDialog = false;
      this.dialogLoading = false;
      this.clearAllFields();
    },

    /**
     * Clears all form fields to default values
     */
    clearAllFields() {
      this.errorMessages = {
        feature: "",
        schedulerTiming: "",
        roles: "",
        locations: "",
      };
      this.editNotificationData = null;
    },

    /**
     * Resets component data to initial state
     */
    resetComponentData() {
      this.loading = false;
      this.error = null;
      this.showNotificationDialog = false;
      this.editNotificationData = null;
      this.dialogLoading = false;
      this.clearAllFields();
    },
  },
};
</script>

<style scoped>
.notification-loading,
.notification-error {
  padding: 40px;
  text-align: center;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
